/**
 * Error reporting to Sentry, on only where the deployment serves a DSN. The SDK loads on demand,
 * so a page that reports nothing never downloads it.
 */

import type * as SentryModule from '@sentry/react';
import type { SeverityLevel } from '@sentry/react';
import type { ErrorInfo } from 'react';

import type { GradedViolation } from '@/types/contract';
import { ViolationSeverity } from '@/types/contract';
import type { SentryConfig, SentryIdentity } from '@/types/monitoring';

import { getIssueText, getViolationText } from './violationText';

type SentrySdk = typeof SentryModule;

type Report = (sdk: SentrySdk) => void;

/** Enough for a failing boot; the limit stops an error loop on a slow load. */
export const MAX_PENDING_REPORTS = 100;

const HEX_RADIX = 16;
const HEX_DIGITS_PER_BYTE = 2;

/** Sentry counts and groups a user's errors by this id, and never receives the email. */
export async function hashEmail(email: string): Promise<string> {
    const digest = await crypto.subtle.digest(
        'SHA-256', new TextEncoder().encode(email.trim().toLowerCase()),
    );
    return Array.from(
        new Uint8Array(digest),
        (byte) => byte.toString(HEX_RADIX).padStart(HEX_DIGITS_PER_BYTE, '0'),
    ).join('');
}

function getViolationLevel(severity: ViolationSeverity): SeverityLevel {
    switch (severity) {
        case ViolationSeverity.Major:
            return 'error';
        case ViolationSeverity.Minor:
            return 'warning';
    }
}

async function identifyUser(sdk: SentrySdk, identity: SentryIdentity): Promise<void> {
    sdk.setTag('org_id', identity.getActiveOrg());
    const email = identity.getUser()?.email ?? '';
    if (email === '') {
        sdk.setUser(null);
        return;
    }
    const id = await hashEmail(email);
    // A sign-out or a different sign-in during the hash makes this id stale.
    if (identity.getUser()?.email === email) {
        sdk.setUser({ id });
    }
}

function fileConsoleError(sdk: SentrySdk, args: unknown[]): void {
    const extra = { arguments: args };
    const error = args.find((arg) => arg instanceof Error);
    if (error !== undefined) {
        sdk.captureException(error, { extra });
        return;
    }
    sdk.captureMessage(args.filter((arg) => typeof arg === 'string').join(' '), {
        level: 'error',
        extra,
    });
}

/**
 * Files the page's errors. The first render does not wait for the SDK, so what fails before the
 * SDK runs waits in a queue.
 */
export class SentryReporter {
    private sdk: SentrySdk | null = null;

    /** The reports that wait for the SDK; null once it runs or once the reporter stops. */
    private pending: Report[] | null = [];

    private logToConsole: typeof console.error | null = null;

    private readonly onWindowError = (event: ErrorEvent): void => {
        this.file((sdk): void => {
            sdk.captureException(event.error);
        });
    };

    private readonly onUnhandledRejection = (event: PromiseRejectionEvent): void => {
        this.file((sdk): void => {
            sdk.captureException(event.reason);
        });
    };

    /** Keeps the page's errors from its start, for start() to file or stop() to drop. */
    collect(): void {
        if (this.logToConsole !== null) {
            return;
        }
        const logToConsole = console.error;
        this.logToConsole = logToConsole;
        console.error = (...args: unknown[]): void => {
            logToConsole.apply(console, args);
            // React logs a crash it caught just before componentDidCatch. The boundary files the
            // crash with its component stack first, and Sentry drops the second capture.
            queueMicrotask((): void => {
                this.file((sdk): void => {
                    fileConsoleError(sdk, args);
                });
            });
        };
        window.addEventListener('error', this.onWindowError);
        window.addEventListener('unhandledrejection', this.onUnhandledRejection);
    }

    async start(config: SentryConfig, identity: SentryIdentity): Promise<void> {
        this.collect();
        const sdk = await import('@sentry/react');
        sdk.init({
            dsn: config.dsn,
            environment: config.environment,
            release: __UI_VERSION__ === '' ? undefined : __UI_VERSION__,
            // The user is the hashed id that identifyUser sets, never the IP or the cookies.
            dataCollection: { userInfo: false, cookies: false },
        });
        // The SDK's own handlers file the uncaught errors from here on.
        this.removeWindowListeners();
        await identifyUser(sdk, identity);
        const pendingReports = this.pending ?? [];
        this.pending = null;
        this.sdk = sdk;
        for (const report of pendingReports) {
            report(sdk);
        }
        identity.subscribe((): void => {
            identifyUser(sdk, identity).catch((error: unknown) => {
                console.error('[sentry] cannot identify the user', error);
            });
        });
    }

    /** Drops the kept reports where the page reports nothing: no DSN, or no SDK. */
    stop(): void {
        this.pending = null;
        if (this.logToConsole !== null) {
            console.error = this.logToConsole;
            this.logToConsole = null;
        }
        this.removeWindowListeners();
    }

    /** Files a crash that an error boundary caught, with the component stack React gives it. */
    reportReactCrash(error: unknown, info: ErrorInfo): void {
        this.file((sdk): void => {
            sdk.captureReactException(error, info);
        });
    }

    /** Files a contract violation that the page does not show; one Sentry issue per endpoint. */
    reportContractViolation(violation: GradedViolation): void {
        this.file((sdk): void => {
            sdk.captureMessage(`Contract violation: ${getViolationText(violation)}`, {
                level: getViolationLevel(violation.severity),
                fingerprint: ['contract-violation', violation.endpoint],
                tags: {
                    violation_kind: violation.kind,
                    violation_source: violation.source.kind,
                },
                contexts: {
                    contract_violation: {
                        endpoint: violation.endpoint,
                        source: violation.source,
                        severity: violation.severity,
                        // Sentry cuts contexts three levels deep, so an issue object would not
                        // arrive.
                        issues: violation.issues.map(getIssueText),
                    },
                },
            });
        });
    }

    private file(report: Report): void {
        if (this.sdk !== null) {
            report(this.sdk);
            return;
        }
        if (this.pending !== null && this.pending.length < MAX_PENDING_REPORTS) {
            this.pending.push(report);
        }
    }

    private removeWindowListeners(): void {
        window.removeEventListener('error', this.onWindowError);
        window.removeEventListener('unhandledrejection', this.onUnhandledRejection);
    }
}

export const sentryReporter = new SentryReporter();
